/**
 * Separates component props between React Aria/Stately hooks and the root
 * element, with a hand-maintained map of hook prop names.
 *
 * Covers hooks from `@react-aria/*` and `@react-stately/*`. Each array lists
 * the property keys that the corresponding hook type accepts. These are derived
 * from the actual `.d.ts` files and verified by type tests in
 * `filterProps.type.spec.ts`.
 *
 * When adopting a new hook, add its prop array, update `ReactAriaHookProps`
 * and `hookPropArrays` — the type test covers it automatically.
 */

import type { AriaButtonProps } from '@react-aria/button';
import type {
  AriaCheckboxGroupItemProps,
  AriaCheckboxGroupProps,
  AriaCheckboxProps,
} from '@react-aria/checkbox';
import type { AriaFocusRingProps } from '@react-aria/focus';
import type { HoverProps } from '@react-aria/interactions';
import type { AriaLinkOptions } from '@react-aria/link';
import type { AriaProgressBarProps } from '@react-aria/progress';
import type { AriaRadioGroupProps, AriaRadioProps } from '@react-aria/radio';
import type { SeparatorProps } from '@react-aria/separator';
import type { AriaTextFieldOptions } from '@react-aria/textfield';

// ---------------------------------------------------------------------------
// Type map: hook name → prop interface (type-level only, erased at build)
// ---------------------------------------------------------------------------

export interface ReactAriaHookProps {
  useButton: AriaButtonProps;
  useCheckbox: AriaCheckboxProps;
  useCheckboxGroup: AriaCheckboxGroupProps;
  useCheckboxGroupItem: AriaCheckboxGroupItemProps;
  useFocusRing: AriaFocusRingProps;
  useHover: HoverProps;
  useLink: AriaLinkOptions;
  useProgressBar: AriaProgressBarProps;
  useRadio: AriaRadioProps;
  useRadioGroup: AriaRadioGroupProps;
  useSeparator: SeparatorProps;
  useTextField: AriaTextFieldOptions<'input'>;
}

// ---------------------------------------------------------------------------
// Runtime prop arrays (const for literal types, used in type tests)
// ---------------------------------------------------------------------------

export const useButtonProps = [
  // ButtonProps > PressEvents
  'onPress',
  'onPressStart',
  'onPressEnd',
  'onPressChange',
  'onPressUp',
  'onClick',
  // ButtonProps > FocusableProps > FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // ButtonProps > FocusableProps > KeyboardEvents
  'onKeyDown',
  'onKeyUp',
  // ButtonProps > FocusableProps
  'autoFocus',
  // ButtonProps
  'isDisabled',
  // LinkButtonProps > AriaButtonElementTypeProps
  'elementType',
  // LinkButtonProps
  'href',
  'target',
  'rel',
  // AriaBaseButtonProps > FocusableDOMProps
  'excludeFromTabOrder',
  // AriaBaseButtonProps > AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // AriaBaseButtonProps
  'aria-disabled',
  'aria-expanded',
  'aria-haspopup',
  'aria-controls',
  'aria-pressed',
  'aria-current',
  'type',
  'preventFocusOnPress',
  'form',
  'formAction',
  'formEncType',
  'formMethod',
  'formNoValidate',
  'formTarget',
  'name',
  'value',
] as const;

export const useCheckboxProps = [
  // ToggleStateOptions > InputBase
  'isDisabled',
  'isReadOnly',
  // ToggleStateOptions
  'defaultSelected',
  'isSelected',
  'onChange',
  // Validation<boolean>
  'isRequired',
  'isInvalid',
  'validationState',
  'validationBehavior',
  'validate',
  // FocusableProps > FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // FocusableProps > KeyboardEvents
  'onKeyDown',
  'onKeyUp',
  // FocusableProps
  'autoFocus',
  // ToggleProps
  'value',
  // CheckboxProps
  'isIndeterminate',
  // InputDOMProps
  'name',
  'form',
  // AriaToggleProps > FocusableDOMProps
  'excludeFromTabOrder',
  // AriaToggleProps > AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // AriaToggleProps > AriaValidationProps
  'aria-errormessage',
  // AriaToggleProps > PressEvents
  'onPress',
  'onPressStart',
  'onPressEnd',
  'onPressChange',
  'onPressUp',
  'onClick',
  // AriaToggleProps
  'aria-controls',
] as const;

export const useCheckboxGroupProps = [
  // ValueBase<string[]>
  'value',
  'defaultValue',
  'onChange',
  // Pick<InputDOMProps, 'name'> + InputDOMProps
  'name',
  'form',
  // InputBase
  'isDisabled',
  'isReadOnly',
  // LabelableProps
  'label',
  // HelpTextProps
  'description',
  'errorMessage',
  // Validation<string[]>
  'isRequired',
  'isInvalid',
  'validationState',
  'validationBehavior',
  'validate',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // AriaValidationProps
  'aria-errormessage',
  // FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
] as const;

export const useCheckboxGroupItemProps = [
  // Same as useCheckbox minus 'isSelected' and 'defaultSelected'
  // ToggleStateOptions > InputBase
  'isDisabled',
  'isReadOnly',
  // ToggleStateOptions (minus isSelected/defaultSelected)
  'onChange',
  // Validation<boolean>
  'isRequired',
  'isInvalid',
  'validationState',
  'validationBehavior',
  'validate',
  // FocusableProps > FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // FocusableProps > KeyboardEvents
  'onKeyDown',
  'onKeyUp',
  // FocusableProps
  'autoFocus',
  // ToggleProps
  'value',
  // CheckboxProps
  'isIndeterminate',
  // InputDOMProps
  'name',
  'form',
  // AriaToggleProps > FocusableDOMProps
  'excludeFromTabOrder',
  // AriaToggleProps > AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // AriaToggleProps > AriaValidationProps
  'aria-errormessage',
  // AriaToggleProps > PressEvents
  'onPress',
  'onPressStart',
  'onPressEnd',
  'onPressChange',
  'onPressUp',
  'onClick',
  // AriaToggleProps
  'aria-controls',
] as const;

export const useFocusRingProps = [
  'within',
  'isTextInput',
  'autoFocus',
] as const;

export const useHoverProps = [
  // HoverEvents
  'onHoverStart',
  'onHoverEnd',
  'onHoverChange',
  // HoverProps
  'isDisabled',
] as const;

export const useLinkProps = [
  // FocusableProps > FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // FocusableProps > KeyboardEvents
  'onKeyDown',
  'onKeyUp',
  // FocusableProps
  'autoFocus',
  // PressEvents
  'onPress',
  'onPressStart',
  'onPressEnd',
  'onPressChange',
  'onPressUp',
  'onClick',
  // LinkDOMProps
  'href',
  'hrefLang',
  'target',
  'rel',
  'download',
  'ping',
  'referrerPolicy',
  'routerOptions',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // AriaLinkOptions own
  'isDisabled',
  'elementType',
] as const;

export const useProgressBarProps = [
  // ProgressBaseProps
  'value',
  'minValue',
  'maxValue',
  // ProgressBarBaseProps
  'label',
  'formatOptions',
  'valueLabel',
  // ProgressBarProps
  'isIndeterminate',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
] as const;

export const useRadioProps = [
  // FocusableProps > FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // FocusableProps > KeyboardEvents
  'onKeyDown',
  'onKeyUp',
  // FocusableProps
  'autoFocus',
  // RadioProps
  'value',
  'isDisabled',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // PressEvents
  'onPress',
  'onPressStart',
  'onPressEnd',
  'onPressChange',
  'onPressUp',
  'onClick',
] as const;

export const useRadioGroupProps = [
  // ValueBase<string|null, string>
  'value',
  'defaultValue',
  'onChange',
  // InputBase
  'isDisabled',
  'isReadOnly',
  // Pick<InputDOMProps, 'name'> + InputDOMProps
  'name',
  'form',
  // Validation<string>
  'isRequired',
  'isInvalid',
  'validationState',
  'validationBehavior',
  'validate',
  // LabelableProps
  'label',
  // HelpTextProps
  'description',
  'errorMessage',
  // FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // RadioGroupProps
  'orientation',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // AriaValidationProps
  'aria-errormessage',
] as const;

export const useSeparatorProps = [
  // SeparatorProps
  'orientation',
  'elementType',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
] as const;

export const useTextFieldProps = [
  // InputBase
  'isDisabled',
  'isReadOnly',
  // Validation<string>
  'isRequired',
  'isInvalid',
  'validationState',
  'validationBehavior',
  'validate',
  // HelpTextProps
  'description',
  'errorMessage',
  // FocusableProps > FocusEvents
  'onFocus',
  'onBlur',
  'onFocusChange',
  // FocusableProps > KeyboardEvents
  'onKeyDown',
  'onKeyUp',
  // FocusableProps
  'autoFocus',
  // TextInputBase
  'placeholder',
  // ValueBase<string>
  'value',
  'defaultValue',
  'onChange',
  // LabelableProps
  'label',
  // AriaLabelingProps
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'aria-details',
  // FocusableDOMProps
  'excludeFromTabOrder',
  // TextInputDOMProps > InputDOMProps
  'name',
  'form',
  // TextInputDOMProps > TextInputDOMEvents
  'onCopy',
  'onCut',
  'onPaste',
  'onCompositionStart',
  'onCompositionEnd',
  'onCompositionUpdate',
  'onSelect',
  'onBeforeInput',
  'onInput',
  // TextInputDOMProps
  'autoComplete',
  'maxLength',
  'minLength',
  'pattern',
  'type',
  'inputMode',
  'autoCorrect',
  'spellCheck',
  // AriaValidationProps
  'aria-errormessage',
  // AriaTextFieldProps
  'aria-activedescendant',
  'aria-autocomplete',
  'aria-haspopup',
  'aria-controls',
  'enterKeyHint',
  // AriaTextFieldOptions
  'inputElementType',
  'autoCapitalize',
] as const;

// ---------------------------------------------------------------------------
// Exported const map: hook name → prop tuple (preserves literal types)
// ---------------------------------------------------------------------------

/**
 * Single source of truth mapping every hook to its prop array.
 * `satisfies` enforces that every key in `ReactAriaHookProps` has an entry —
 * adding a new hook without its array is a compile error. The type spec's
 * mapped-type guard then automatically covers the new hook.
 */
export const hookPropArrays = {
  useButton: useButtonProps,
  useCheckbox: useCheckboxProps,
  useCheckboxGroup: useCheckboxGroupProps,
  useCheckboxGroupItem: useCheckboxGroupItemProps,
  useFocusRing: useFocusRingProps,
  useHover: useHoverProps,
  useLink: useLinkProps,
  useProgressBar: useProgressBarProps,
  useRadio: useRadioProps,
  useRadioGroup: useRadioGroupProps,
  useSeparator: useSeparatorProps,
  useTextField: useTextFieldProps,
} as const satisfies Record<keyof ReactAriaHookProps, readonly string[]>;

// ---------------------------------------------------------------------------
// Runtime map: hook name → Set of prop names (derived from hookPropArrays)
// ---------------------------------------------------------------------------

const REACT_ARIA_PROPS = Object.fromEntries(
  Object.entries(hookPropArrays).map(([k, v]) => [k, new Set(v)])
) as unknown as Record<keyof ReactAriaHookProps, ReadonlySet<string>>;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HookName = keyof ReactAriaHookProps;

/**
 * Props that appear on React Aria hook interfaces but are deliberately
 * omitted from the runtime prop arrays, so they flow to `localProps` instead
 * of any hook bucket. Single source of truth — `filterProps.typetest.ts`
 * imports this to enforce the same exclusion in its array-vs-interface check.
 *
 * - `children`: present on several RA types but no hook actually processes it.
 * - `id`: present via `DOMProps`, but consumers expect `id` on the root
 *   element. In multi-element components the hook target may be a hidden
 *   input, so claiming `id` would place it on the wrong element.
 */
export type ExcludedHookKeys = 'children' | 'id';

/** Keys a hook claims at runtime — interface keys minus the excluded set. */
type ClaimedHookKeys<Hook extends HookName> = Exclude<
  keyof ReactAriaHookProps[Hook],
  ExcludedHookKeys
>;

/** All prop keys claimed by any of the listed hooks. */
type ClaimedKeys<Hooks extends readonly HookName[]> = ClaimedHookKeys<
  Hooks[number]
>;

/** Props remaining after all hooks have claimed theirs. */
type LocalProps<T extends object, Hooks extends readonly HookName[]> = Omit<
  T,
  ClaimedKeys<Hooks>
>;

/** Props for one hook, typed to the hook's expected types (not the input's). */
type HookProps<T extends object, Hook extends HookName> = Pick<
  ReactAriaHookProps[Hook],
  Extract<keyof T, ClaimedHookKeys<Hook>>
>;

/** Tuple: [localProps, ...per-hook props in declaration order]. */
type FilterPropsResult<T extends object, Hooks extends readonly HookName[]> = [
  LocalProps<T, Hooks>,
  ...{ [I in keyof Hooks]: HookProps<T, Hooks[I] & HookName> },
];

// ---------------------------------------------------------------------------
// Runtime
// ---------------------------------------------------------------------------

/**
 * Separates component props into local (root) props and per-hook React Aria
 * prop objects.
 *
 * The first element of the returned array is always the local props —
 * everything not claimed by any listed hook. Subsequent elements correspond
 * positionally to the hooks array.
 *
 * Props that appear in multiple hooks are assigned to every matching bucket.
 * Props not recognised by any hook fall through to local.
 *
 * @example
 * ```ts
 * const [localProps, checkboxRaProps, hoverRaProps] = filterProps(props, ['useCheckbox', 'useHover']);
 * ```
 */
export function filterProps<
  T extends object,
  const Hooks extends readonly HookName[],
>(props: T, hooks: Hooks): FilterPropsResult<T, Hooks> {
  const hookSets = hooks.map((hook) => REACT_ARIA_PROPS[hook]);
  const hookBuckets: Record<string, unknown>[] = hooks.map(() => ({}));
  const localProps: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    let claimed = false;

    for (let i = 0; i < hookSets.length; i++) {
      if (hookSets[i].has(key)) {
        hookBuckets[i][key] = value;
        claimed = true;
      }
    }

    if (!claimed) {
      localProps[key] = value;
    }
  }

  // TS can't track string keys through a dynamic loop — the cast is verified
  // by type tests and runtime tests in filterProps.spec.ts.
  return [localProps, ...hookBuckets] as unknown as FilterPropsResult<T, Hooks>;
}
